/**
 * Office unprotect — UI layer.
 *
 * The one thing this page must never blur: editing protection and encryption are
 * different, and only one of them is removable. An encrypted file gets its own
 * outcome with its own explanation, never a generic failure.
 *
 * What a file IS comes first, and from its bytes (23.10.2026). Report of 21.10 («не
 * открывает», a .docx whose bytes held no document at all): the page knew one road —
 * «a zip of parts» — and every other file met a red card titled with a raw key,
 * «verdict_not_a_package». The same shared classifier /office-repair uses
 * (file-anatomy.js → route.js) now answers first: a file of zeros, Word's «~$» owner
 * file, another format under a Word name, a damaged file — each is said as what it is,
 * and a damaged one goes to «Ремонт документов Office» WITH the file (tool-handoff),
 * because recovering damage is that tool's job, not this one's. Every key shown is a
 * literal (tests/Feature/OfficeUnprotectAnswersTest.php checks them in all locales).
 */

import { examine, unprotect } from './ooxml-unprotect.js';
import { fileAnatomy, reportAnatomy } from './file-anatomy.js';
import { routeFile } from './route.js';
import { recoverBinaryText } from './binary-text.js';

const T = (k, p) => {
  let s = (window.__UP_I18N && window.__UP_I18N[k]) || k;
  if (p) for (const [a, b] of Object.entries(p)) s = s.replace(new RegExp(':' + a, 'g'), b);
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

let current = null;
let officeDoor = null;

const TWIN = { word: 'DOCX', sheets: 'XLSX', slides: 'PPTX' };

/* One telemetry row per file: what its bytes were and which answer it got. */
function note(file, anatomy, at, extra) {
  reportAnatomy(file, at, extra, { slug: 'office-unprotect', f: 'up_anatomy', anatomy: anatomy || { sig: 'unreadable' } });
}

function human(bytes) {
  const u = [T('or_unit_b'), T('or_unit_kb'), T('or_unit_mb'), T('or_unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

/* The way to the repair tool, carrying the file along (no second pick). */
function repairDoor() {
  const url = window.__UP_REPAIR_URL;
  return url
    ? `<div class="up-actions"><a class="up-btn" id="up-repair" href="${esc(url)}" data-slug="office-repair">${esc(T('repair_door'))}</a></div>`
    : '';
}
function wireRepairDoor(file) {
  const a = el('up-repair');
  if (a && window.__toolHandoff && typeof window.__toolHandoff.carry === 'function') {
    window.__toolHandoff.carry(a, () => ({ blob: file, filename: file.name }));
  }
}

/* An old binary .doc/.xls/.ppt: not encrypted, and not a format this tool edits.
 * Said plainly, with the way out that works without anything from us (save it as
 * the modern twin in Word/Excel/LibreOffice); while the office lane is on, its
 * door follows — Premium gets the twin from our server, it comes back into the
 * zone, and the ordinary run below removes the lock. Fixture check: a sheet
 * protected in .xls keeps its <sheetProtection> when saved as .xlsx, and this tool
 * removes it (tests/js/ooxml-unprotect.test.mjs). */
async function showLegacy(file, info) {
  const twin = TWIN[info.family] || 'DOCX';
  window.__ae && window.__ae('feature_use', { slug: 'office-unprotect', f: 'legacy_binary', fmt: info.legacy });
  show('warn', T('verdict_legacy', { fmt: String(info.legacy).toUpperCase() }), T('body_legacy', { twin }),
    '<div id="up-office"></div>');
  const OL = window.OfficeLane;
  const host = el('up-office');
  if (!OL || !host || !OL.available()) return;
  if (officeDoor) { try { officeDoor.destroy(); } catch (e) { /* gone */ } }
  officeDoor = OL.door(host, {
    file, tool: 'office-unprotect', mode: 'upgrade',
    info: { status: 'legacy', family: info.family, format: info.legacy, encrypted: false },
    onResult: (out) => {
      const zone = el('upDrop');
      if (zone && typeof zone.__uploaderInjectFile === 'function') zone.__uploaderInjectFile(out, 'office_upgrade');
      else run(out, { upgraded: true });
    },
  });
}

/* A file that is not an Office package at all: what it is, and where it goes. */
async function answerNotAPackage(file, a, r) {
  switch (r.route) {
    case 'zeros':
      note(file, a, 'zeros');
      return show('bad', T('or_class_zeros_title'), T('or_class_zeros_body', { size: human(file.size) }));
    case 'lock':
      note(file, a, 'lock');
      return show('warn', T('or_class_lock_title'), T('or_class_lock_body', { name: esc(String(file.name || '').replace(/^~\$/, '')) }));
    case 'foreign': {
      note(file, a, 'foreign', { as: r.as });
      const fmt = T(r.as === 'csv' ? 'or_fmt_csv' : 'or_fmt_' + r.format);
      show('warn', T('or_class_foreign_title', { format: fmt }), T('body_foreign'), repairDoor());
      return wireRepairDoor(file);
    }
    default: {
      // The start of the file is gone, or nothing names its format: is any text left in it?
      let words = 0;
      try { words = recoverBinaryText(new Uint8Array(await file.arrayBuffer())).words; } catch (_) { words = 0; }
      if (!words) {
        note(file, a, 'nothing');
        return show('bad', T('or_class_unknown_title'), T('or_class_unknown_body'));
      }
      note(file, a, 'damaged_text', { words });
      show('warn', T('verdict_damaged'), T('body_damaged'), repairDoor());
      return wireRepairDoor(file);
    }
  }
}

async function run(file, opts) {
  const upgraded = !!(opts && opts.upgraded);
  if (officeDoor) { try { officeDoor.destroy(); } catch (e) { /* gone */ } officeDoor = null; }
  const out = el('up-out');
  out.innerHTML = `<div class="up-working">${T('working')}</div>`;
  out.hidden = false;
  window.__stage && window.__stage('analyzing');

  let a = null;
  try { a = await fileAnatomy(file); } catch (_) { a = null; }
  const r = routeFile(a, file.name);
  // A zip of parts or a compound file is this tool's to examine; anything else is answered by what it is.
  if (a && r.route !== 'package' && r.route !== 'binary' && r.route !== 'encrypted') return answerNotAPackage(file, a, r);

  let info;
  try {
    info = await examine(file);
  } catch (err) {
    // A zip with no document inside it — or one that does not open at all: damage, not protection.
    note(file, a, 'damaged', { code: String((err && err.code) || 'error').slice(0, 30) });
    show('warn', T('verdict_damaged'), T('body_damaged'), repairDoor());
    return wireRepairDoor(file);
  }

  if (info.encrypted) {
    window.__ae && window.__ae('tool_blocked', { slug: 'office-unprotect', reason: 'encrypted', size: file.size });
    note(file, a, 'encrypted');
    return show('warn', T('verdict_encrypted'), T('body_encrypted'));
  }
  if (info.legacy) { note(file, a, 'legacy'); return showLegacy(file, info); }
  if (!info.locks.length) {
    if (upgraded) {
      // The twin came back with no lock in it: the conversion itself left the
      // restriction behind. That file IS the result — offer it.
      note(file, a, 'none_upgraded');
      current = { blob: file, filename: file.name };
      show('ok', T('verdict_none_upgraded', { kind: String(info.kind).toUpperCase() }),
        T('body_none_upgraded', { kind: String(info.kind).toUpperCase() }),
        `<div class="up-actions"><button type="button" class="up-btn" id="up-save">${T('save')}</button></div>`);
      el('up-save').addEventListener('click', () => window.__downloadFile(current.blob, current.filename));
      return;
    }
    note(file, a, 'none', { kind: info.kind });
    return show('ok', T('verdict_none'), T('body_none', { kind: info.kind }));
  }

  /* A run is spent only when a lock is actually removed. Answering "your file is
   * not protected" must never cost the user their day's run. */
  const _run = window.__toolLimit
    ? window.__toolLimit.begin('office-unprotect',
        window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null)
    : null;
  if (window.__toolLimit && !_run) {
    // The wall is the limit block's to show; «Разбираем документ…» must not stay behind it forever.
    note(file, a, 'limit');
    out.innerHTML = '';
    out.hidden = true;
    return;
  }

  try {
    const res = await unprotect(file, { examined: info, name: file.name });
    current = res;
    const list = res.removed.map((rm) =>
      `<li>${T(LOCK_KEY[rm.tag] || 'lock_other')} — <code>${esc(rm.path)}</code></li>`).join('');
    const hashed = info.locks.some((l) => l.hashed);
    show('ok', T('verdict_removed'),
      T('body_removed', { n: String(res.removed.length) }) + (hashed ? ' ' + T('body_hashed') : ''),
      `<ul class="up-list">${list}</ul>
       <div class="up-actions"><button type="button" class="up-btn" id="up-save">${T('save')}</button></div>`);
    el('up-save').addEventListener('click', () => {
      window.__downloadFile(current.blob, current.filename);
      window.__ae && window.__ae('feature_use', { slug: 'office-unprotect', feature: 'saved' });
    });
    _run && _run.commit();
    note(file, a, 'removed', { kind: info.kind, removed: res.removed.length });
    window.__aeToolComplete && window.__aeToolComplete({
      kind: info.kind, removed: res.removed.length, hashed: hashed ? 1 : 0 });
  } catch (err) {
    _run && _run.abort();
    note(file, a, 'generic', { code: String((err && err.code) || 'error').slice(0, 30) });
    show('bad', T('verdict_generic'), T('body_generic'));
  }
}

/** The lock's label, by element: `w:documentProtection` → «ограничение редактирования Word». */
const LOCK_KEY = {
  sheetProtection: 'lock_sheet', workbookProtection: 'lock_workbook', fileSharing: 'lock_read_only',
  'w:documentProtection': 'lock_word_edit', 'w:writeProtection': 'lock_word_write',
  'p:modifyVerifier': 'lock_ppt_edit',
};

function show(tone, title, body, extra = '') {
  el('up-out').innerHTML = `<div class="up-card up-tone-${tone}">
      <p class="up-title">${title}</p><p class="up-body">${body}</p>${extra}</div>`;
  window.__announce && window.__announce(title);
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('upDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (e) => {
    const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
    if (f) run(f, { upgraded: e.detail.source === 'office_upgrade' });
  });
});

export { run };
